import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(255, 204, 0, 0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #ffcc00;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #ffcc00;
  border-bottom: 1px solid rgba(255, 204, 0, 0.3);
  padding-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #ffcc00;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(255, 204, 0, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(255, 204, 0, 0.5);
  }
`;

export const TheSixthExtinctionMetadata = {
  title: "The Sixth Extinction",
  description: "An exploration of how human activity is driving mass extinction.",
  tags: ['Environment', 'Biology', 'Climate', 'Sustainability'],
  author: "Elizabeth Kolbert",
  datePublished: "2023-08-05",
  category: "Book Review",
  path: "/book-review/the-sixth-extinction",
  coverImage: "/images/sixth_extinction.jpg", // Ensure this path is correct
};

const TheSixthExtinction = () => {
  const navigate = useNavigate();
  const metadata = TheSixthExtinctionMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
          {metadata.tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            "The Sixth Extinction" is a Pulitzer Prize-winning book by Elizabeth Kolbert that explores the concept of mass extinction
            and the human activities driving it. Kolbert draws on her extensive research and travels to provide a comprehensive
            and engaging account of how humanity's actions are accelerating the loss of species at an alarming rate.
          </p>
          <br />
          <p>
            The book combines scientific insight with storytelling as Kolbert documents the decline of various species across the globe.
            From amphibians to coral reefs, she discusses the impact of climate change, deforestation, and pollution on ecosystems.
          </p>
          <br />
          <p>
            Kolbert’s work highlights the urgent need for conservation and changes in policy to combat the ongoing environmental crisis.
            It serves as both a warning and a call to action, emphasizing the need to address the root causes of biodiversity loss.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>Mass Extinction:</strong> The unprecedented rate of species loss caused by human activity.</ListItem>
            <ListItem><strong>Impact of Climate Change:</strong> How rising temperatures and changing weather patterns affect ecosystems.</ListItem>
            <ListItem><strong>Ecological Consequences:</strong> The effects of deforestation, pollution, and habitat destruction on biodiversity.</ListItem>
            <ListItem><strong>Evolutionary Biology:</strong> Insights into how species adapt—or fail to adapt—to environmental changes.</ListItem>
            <ListItem><strong>Conservation Efforts:</strong> The importance of protecting endangered species and habitats.</ListItem>
            <ListItem><strong>Human Responsibility:</strong> The role of humanity in reversing or exacerbating the current ecological crisis.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "Right now, in the astonishingly brief period since humans appeared on earth, we are seeing a massive spasm of extinction."
          </Quote>
          <Quote>
            "In pushing other species to extinction, humanity is busy sawing off the limb on which it perches."
          </Quote>
        </SectionContent>
      </ContentSection>

      {/* Personal Reflection Section */}
      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            Reading "The Sixth Extinction" was both eye-opening and unsettling. The urgency with which Elizabeth Kolbert writes about 
            the current ecological crisis really hit home for me. It made me reflect on how much human activity, from deforestation 
            to pollution, is rapidly accelerating the extinction of species across the globe.
          </p>
          <br />
          <p>
            One of the key takeaways for me was the sheer scale of the impact that climate change and human intervention are having 
            on biodiversity. While the statistics and facts were alarming, it was Kolbert’s storytelling that made the crisis feel 
            personal—seeing how individual species are being wiped out due to factors largely within our control.
          </p>
          <br />
          <p>
            This book reinforced my growing awareness of the role we all play in this crisis. It’s a stark reminder that our actions 
            have long-term consequences for the planet and for future generations. Kolbert’s exploration of extinction is not just 
            about species loss but about the fragility of the ecosystems that sustain all life—including human life.
          </p>
          <br />
          <p>
            As I think about how to act on the knowledge from this book, it reminds me that conservation efforts and environmental 
            policies are not just about preserving individual species—they're about protecting the intricate web of life that makes 
            the Earth habitable. This book has given me a new sense of responsibility toward the environment and a motivation to 
            be more mindful in my daily choices.
          </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default TheSixthExtinction;